'use client';

import type { Service } from '@ahm/api-wrappers-core';
import { pad } from '@ahm/common-helpers';
import {
  Button,
  type ComboboxItem,
  Divider,
  Group,
  Select,
  Stack,
  Text,
  rem,
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import dayjs, { unix } from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/vi';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import classes from './booking-date-time-picker.module.css';
import { BookingTimeSelectOption } from './booking-time-select-option';
import IconTruckBan from '@/assets/icons/truck-ban.svg';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type BookingDateTimePickerProps = {
  onSchedule: (orderTime: number) => void;
  initialTime: number | null;
  maxDayDuration?: Service['max_idle_until'];
};

const DEFAULT_MAX_IDLE_UNTIL = 7;

const BookingDateTimePicker = ({
  onSchedule,
  initialTime = 0,
  maxDayDuration = DEFAULT_MAX_IDLE_UNTIL,
}: BookingDateTimePickerProps) => {
  const isMobile = useIsMobile();
  const tCommon = useTranslations('common');

  const { isServiceDurationBan, truckBan, serviceId } = useBookingStore(
    (state) => state
  );

  const currentDate = useMemo(() => new Date(), []);
  const currentHour = dayjs().hour();
  const limitOffsetByMinutes = 30;
  const thresholdHourByMinutes = 30;
  const curLocale = useLocale();

  const initialDate = initialTime
    ? unix(Number(initialTime)).toDate()
    : currentDate;
  const initialHour = initialTime ? unix(Number(initialTime)).hour() : 0;
  const initialMinute = initialTime ? unix(Number(initialTime)).minute() : 0;

  const [date, setDate] = useState<Date | null>(initialDate);
  const [selectedHour, setSelectedHour] = useState<ComboboxItem | null>(null);
  const [selectedMinute, setSelectedMinute] = useState<ComboboxItem | null>(
    null
  );
  const [hourOptions, setHourOptions] = useState<number[]>([]);
  const [minuteOptions, setMinuteOptions] = useState<number[]>(
    Array.from({ length: 12 }, (_, i) => i * 5)
  ); // default minutes options 0,5,10,...,55

  // Utility to generate hours options
  const generateHours = (from: number, to: number) => {
    const result: number[] = [];
    for (let i = from; i <= to; i++) {
      result.push(i);
    }
    return result;
  };

  const roundMinute = (minute: number) => {
    // Check if the minute is divisible by 5
    if (minute % 5 === 0) {
      return minute;
    } else {
      // If not divisible, round up to the nearest multiple of 5
      return Math.ceil(minute / 5) * 5;
    }
  };

  const disableExcludeDate = useMemo(() => {
    const today = dayjs().startOf('day');
    const maxDate = today.add(maxDayDuration, 'day');

    return (date: Date) =>
      dayjs(date).isBefore(today) || dayjs(date).isAfter(maxDate);
  }, [maxDayDuration]);

  const fetchInitialData = async () => {
    const banHour =
      isServiceDurationBan && truckBan?.ban_hours?.length
        ? truckBan?.ban_hours?.find(
            (banHour) => banHour.from <= currentHour && banHour.to > currentHour
          )
        : null;

    if (!initialTime) {
      const newHour = banHour ? banHour.to : 0;
      const newMinute = banHour ? 5 : 0;
      setDate(currentDate);
      setSelectedHour({
        label: newHour.toString(),
        value: newHour.toString(),
      });
      setSelectedMinute({
        label: newMinute.toString(),
        value: newMinute.toString(),
      });
    } else {
      setDate(initialDate);
      await setSelectedHour({
        label: initialHour.toString(),
        value: initialHour.toString(),
      });
      await setSelectedMinute({
        label: initialMinute.toString(),
        value: initialMinute.toString(),
      });
    }
  };

  useEffect(() => {
    void fetchInitialData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTime, isServiceDurationBan, truckBan, serviceId]);

  const isSelectedSameBanTime = useMemo(() => {
    const { ban_hours } = truckBan ?? {};

    if (!ban_hours?.length) return false;

    const selectedValue = Number(selectedHour?.value) ?? 0;
    const selectedMinuteValue = Number(selectedMinute?.value) ?? 0;

    return ban_hours.some(
      (banHour) =>
        (banHour.from <= selectedValue && banHour.to > selectedValue) ||
        (banHour.to === selectedValue && selectedMinuteValue === 0)
    );
  }, [truckBan, selectedHour?.value, selectedMinute?.value]);

  const updateHourOptions = useMemo(() => {
    return () => {
      const currentDate = new Date();
      const currentMinute = dayjs().minute();
      const lastAvailableDay = dayjs(currentDate).add(maxDayDuration, 'day');
      const isLastAvailableDay = dayjs(date).isSame(lastAvailableDay, 'day');

      let updatedHourOptions: number[] = [];

      if (date && date.toDateString() === currentDate.toDateString()) {
        updatedHourOptions = generateHours(
          currentMinute > 30 ? currentHour + 1 : currentHour,
          23
        );
      } else if (isLastAvailableDay) {
        updatedHourOptions = generateHours(0, currentHour);
      } else {
        updatedHourOptions = generateHours(0, 23);
      }

      setHourOptions(updatedHourOptions);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, maxDayDuration]);

  useEffect(() => {
    updateHourOptions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, maxDayDuration, truckBan]);

  const updateMinuteOptions = () => {
    const currentHour = dayjs().hour();
    const currentMinute = dayjs().minute();

    const lastAvailableDay = dayjs(currentDate).add(maxDayDuration, 'day');
    const isLastAvailableDay = dayjs(date).isSame(lastAvailableDay, 'day');
    const isToday = dayjs().isSame(dayjs(date), 'day');
    const isSelectedCurrentHour = currentHour === Number(selectedHour?.value);

    let from = 0;
    const to = 55;

    if (isToday) {
      from = calculateFrom(currentHour, currentMinute, isSelectedCurrentHour);
    }

    let newMinuteOptions = generateMinuteOptions(from, to);

    if (isLastAvailableDay && Number(selectedHour?.value) === currentHour) {
      newMinuteOptions = adjustForLastAvailableDay(currentMinute);
    }

    setMinuteOptions(newMinuteOptions);
  };

  const calculateFrom = (currentHour, currentMinute, isSelectedCurrentHour) => {
    let from = 0;

    if (isSelectedCurrentHour) {
      from = roundMinute(currentMinute) + limitOffsetByMinutes;

      if (from >= 60) {
        from -= 60;
        incrementSelectedHour();
      }
    } else if (
      currentHour + 1 === Number(selectedHour?.value) &&
      currentMinute > thresholdHourByMinutes
    ) {
      const x = roundMinute(currentMinute) + limitOffsetByMinutes;
      from = x >= 60 ? x - 60 : x;
    }

    return from;
  };

  const incrementSelectedHour = () => {
    setSelectedHour((prevHour) => {
      if (!prevHour) return null;

      const newValue = Number(prevHour.value) + 1;
      return {
        label: newValue.toString(),
        value: newValue.toString(),
      };
    });
  };

  const generateMinuteOptions = (from: number, to: number) => {
    return Array.from({ length: (to - from) / 5 + 1 }, (_, i) => from + i * 5);
  };

  const adjustForLastAvailableDay = (currentMinute: number) => {
    const roundedCurrentMinute = currentMinute - (currentMinute % 5);
    return Array.from(
      { length: roundedCurrentMinute / 5 + 1 },
      (_, i) => i * 5
    );
  };

  useEffect(() => {
    updateMinuteOptions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, hourOptions, selectedHour?.value, maxDayDuration]);

  const resetSelection = (
    selected: ComboboxItem | null,
    options: number[],
    setSelected: React.Dispatch<React.SetStateAction<ComboboxItem | null>>
  ) => {
    if (
      !options.some((option) => option === Number(selected?.value)) &&
      options.length > 0
    ) {
      setSelected({
        label: options[0].toString(),
        value: options[0].toString(),
      });
    }
  };

  useEffect(() => {
    if (!isServiceDurationBan) {
      resetSelection(selectedHour, hourOptions, setSelectedHour);
      resetSelection(selectedMinute, minuteOptions, setSelectedMinute);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hourOptions, minuteOptions]);

  const combineDateTime = (
    date: Date | null,
    hours: number | null,
    minutes: number | null
  ): Date | null => {
    if (!date) return null;

    const combinedDate = new Date(date);
    combinedDate.setHours(Number(hours));
    combinedDate.setMinutes(Number(minutes));

    return combinedDate;
  };

  const handleConfirmSchedule = () => {
    const combined = combineDateTime(
      date,
      Number(selectedHour?.value),
      Number(selectedMinute?.value)
    );
    const timestamp = dayjs(combined).unix();

    onSchedule(timestamp);
  };

  return (
    <Stack gap={0}>
      <Stack align="center" justify="center" gap="md" p="md">
        <DatePicker
          value={date}
          onChange={setDate}
          defaultDate={date ?? currentDate}
          locale={curLocale}
          excludeDate={disableExcludeDate}
          classNames={classes}
          styles={() => ({
            day: {
              fontSize: 14,
              fontWeight: 500,
            },
            calendarHeader: {
              maxWidth: '100%',
              margin: '0 auto',
              marginBottom: '16px',
            },
            calendarHeaderLevel: {
              fontWeight: 700,
            },
            month: {
              width: '100%',
            },
            monthCell: {
              textAlign: 'center',
            },
            weekday: {
              fontWeight: 700,
            },
          })}
          style={{ margin: isMobile ? '0 auto' : 0 }}
        />

        <Group justify="center" gap="xs">
          <Select
            value={selectedHour ? selectedHour.value : null}
            onChange={(_value, option) => setSelectedHour(option)}
            data={hourOptions.map((hour) => ({
              value: hour.toString(),
              label: pad(hour, 2),
            }))}
            renderOption={BookingTimeSelectOption}
            variant="filled"
            id="hours-select"
            rightSection={<></>}
            rightSectionWidth="0"
            maxDropdownHeight={200}
            allowDeselect={false}
            checkIconPosition="right"
            comboboxProps={{
              position: 'top',
              middlewares: { flip: false, shift: false },
              withinPortal: false,
              width: 216,
            }}
            styles={{
              input: {
                height: 'auto',
                padding: `${rem(5)} ${rem(4)}`,
                fontWeight: 600,
                textAlign: 'center',
                width: 72,
              },
              option: {
                padding: 0,
                width: '100%',
              },
            }}
          />
          <Text size="sm" fw={600}>
            :
          </Text>
          <Select
            value={selectedMinute ? selectedMinute.value : null}
            onChange={(_value, option) => setSelectedMinute(option)}
            data={minuteOptions.map((min) => ({
              value: min.toString(),
              label: pad(min, 2),
            }))}
            renderOption={BookingTimeSelectOption}
            variant="filled"
            id="minutes-select"
            rightSection={<></>}
            rightSectionWidth="0"
            maxDropdownHeight={200}
            allowDeselect={false}
            checkIconPosition="right"
            comboboxProps={{
              position: 'top',
              middlewares: { flip: false, shift: false },
              withinPortal: false,
              width: 216,
            }}
            styles={{
              input: {
                height: 'auto',
                padding: `${rem(5)} ${rem(4)}`,
                fontWeight: 600,
                textAlign: 'center',
                width: 72,
              },
              option: {
                padding: 0,
                width: '100%',
              },
            }}
          />
        </Group>
      </Stack>

      <Divider h={2} />
      {isSelectedSameBanTime && (
        <Group wrap="nowrap" px="md" pt="md">
          <IconTruckBan style={{ width: '24px' }} />
          <Text size="sm" fw={500} c="red.6">
            Không thể lấy hàng trong khung giờ cấm tải. Vui lòng chọn khung giờ
            khác
          </Text>
        </Group>
      )}

      <Group wrap="nowrap" w="100%" p="md">
        <Button
          variant="outline"
          onClick={() => onSchedule(0)}
          size="md"
          h={rem(48)}
          fw={700}
          fullWidth
          color="neutral.3"
          styles={{
            label: {
              color: 'var(--mantine-color-neutral-7)',
              fontSize: rem(14),
            },
          }}
        >
          {tCommon('actions.cancel')}
        </Button>

        <Button
          onClick={handleConfirmSchedule}
          size="md"
          mih={48}
          fullWidth
          fw={700}
          disabled={isSelectedSameBanTime}
        >
          {tCommon('actions.save')}
        </Button>
      </Group>
    </Stack>
  );
};

export default BookingDateTimePicker;
